import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  X, Save, Package, Tag, DollarSign, Ruler, Info, Plus, Minus,
  Layers, Box, Zap, FileText, Shield, Home, Sparkles, Star
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Product } from '@/hooks/useProducts';
import { materials } from '@/constants/materials';

interface ProductModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (product: Omit<Product, 'id' | 'created_at'>) => Promise<void>;
  initialProduct?: Product;
  mode?: 'create' | 'edit';
}

// Wszystkie kategorie z kalkulatora
const productCategories = [
  { 
    id: 'formatka', 
    name: 'Formatka / Płyta', 
    icon: <Layers className="w-5 h-5" />,
    description: 'Płaskie arkusze materiału',
    multiplier: 2.25,
    waste: 0.02
  },
  { 
    id: 'pojemnik', 
    name: 'Pojemnik / Box', 
    icon: <Box className="w-5 h-5" />,
    description: 'Pojemniki, pudełka, organizery',
    multiplier: 2.25,
    waste: 0.05,
    hasOptions: ['wieko', 'przegrody', 'dno_inny_material']
  },
  { 
    id: 'gablota', 
    name: 'Gablota', 
    icon: <FileText className="w-5 h-5" />,
    description: 'Gabloty informacyjne',
    multiplier: 2.5,
    waste: 0.05,
    hasOptions: ['wieko', 'zawiasy', 'zamek']
  },
  { 
    id: 'obudowa', 
    name: 'Obudowa / Osłona', 
    icon: <Shield className="w-5 h-5" />,
    description: 'Osłony, obudowy urządzeń',
    multiplier: 2.35,
    waste: 0.05
  },
  { 
    id: 'ekspozytor', 
    name: 'Ekspozytor', 
    icon: <Package className="w-5 h-5" />,
    description: 'Stojaki reklamowe, ekspozytory',
    multiplier: 2.4,
    waste: 0.08,
    subcategories: [
      { id: 'podstawkowy', name: 'Podstawkowy' },
      { id: 'schodkowy', name: 'Schodkowy' },
      { id: 'z_haczykami', name: 'Z haczykami' },
      { id: 'wiszacy', name: 'Wiszący' },
      { id: 'stojak', name: 'Stojak' },
      { id: 'kosmetyczny', name: 'Kosmetyczny' }
    ]
  },
  { 
    id: 'kaseton', 
    name: 'Kaseton reklamowy', 
    icon: <Zap className="w-5 h-5" />,
    description: 'Kasetony świetlne, reklamy',
    multiplier: 1,
    waste: 0.1,
    hasKasetonType: true
  },
  { 
    id: 'ledon', 
    name: 'Ledon / LED', 
    icon: <Star className="w-5 h-5" />,
    description: 'Produkty z podświetleniem LED',
    multiplier: 2.5,
    waste: 0.05,
    hasLED: true
  },
  { 
    id: 'impuls', 
    name: 'Impuls / Regał', 
    icon: <Home className="w-5 h-5" />,
    description: 'Regały przyścienne',
    multiplier: 2.3,
    waste: 0.06,
    hasShelves: true
  },
  { 
    id: 'niestandardowy', 
    name: 'Produkt niestandardowy', 
    icon: <Sparkles className="w-5 h-5" />,
    description: 'Produkt nietypowy',
    multiplier: 2.5,
    waste: 0.1
  }
];

export const ProductModal: React.FC<ProductModalProps> = ({ 
  isOpen, 
  onClose, 
  onSave, 
  initialProduct,
  mode = 'create' 
}) => {
  const [product, setProduct] = useState<Partial<Product>>(
    initialProduct || {
      name: '',
      category: '',
      subcategory: '',
      material: 'plexi',
      thickness: 3,
      price_per_m2: 0,
      min_order: 0.25,
      max_width: 3050,
      max_height: 2050,
      max_depth: 500,
      description: '',
      images: [],
      tags: [],
      is_active: true,
      popularity: 50,
      multiplier: 2.25,
      waste: 0.05,
      product_params: {}
    }
  );

  const [tagInput, setTagInput] = useState('');
  const [saving, setSaving] = useState(false);

  const selectedCategory = productCategories.find(c => c.id === product.category);

  const handleSave = async () => {
    if (!product.name || !product.category || !product.material) {
      alert('Wypełnij wszystkie wymagane pola');
      return;
    }

    setSaving(true);
    try {
      // Ustaw domyślne wartości na podstawie kategorii
      const category = productCategories.find(c => c.id === product.category);
      
      await onSave({
        ...product,
        multiplier: category?.multiplier || 2.25,
        waste: category?.waste || 0.05,
        price_per_m2: product.price_per_m2 || 0,
        min_order: product.min_order || 0.25,
        max_width: product.max_width || 3050,
        max_height: product.max_height || 2050,
        description: product.description || '',
        images: product.images || [],
        tags: product.tags || [],
        is_active: product.is_active !== false,
        popularity: product.popularity || 50
      } as Omit<Product, 'id' | 'created_at'>);
      
      onClose();
    } catch (error) {
      console.error('Error saving product:', error);
    } finally {
      setSaving(false);
    }
  };

  const handleAddTag = () => {
    if (tagInput.trim()) {
      setProduct(prev => ({
        ...prev,
        tags: [...(prev.tags || []), tagInput.trim()]
      }));
      setTagInput('');
    }
  };

  const handleRemoveTag = (index: number) => {
    setProduct(prev => ({
      ...prev,
      tags: prev.tags?.filter((_, i) => i !== index) || []
    }));
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4"
        onClick={onClose}
      >
        <motion.div
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.9, opacity: 0 }}
          onClick={(e) => e.stopPropagation()}
          className="bg-zinc-800 rounded-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto border border-zinc-700"
        >
          {/* Header */}
          <div className="sticky top-0 bg-zinc-800 border-b border-zinc-700 p-6 flex justify-between items-start">
            <h2 className="text-2xl font-bold text-white">
              {mode === 'create' ? 'Dodaj nowy produkt' : 'Edytuj produkt'}
            </h2>
            <motion.button
              whileHover={{ scale: 1.1, rotate: 90 }}
              whileTap={{ scale: 0.9 }}
              onClick={onClose}
              className="p-2 hover:bg-zinc-700 rounded-lg transition-all"
            >
              <X className="w-5 h-5 text-gray-400" />
            </motion.button>
          </div>

          <div className="p-6 space-y-8">
            {/* Podstawowe informacje */}
            <div>
              <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                <Tag className="w-5 h-5 text-purple-400" />
                Podstawowe informacje
              </h3>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-400 mb-2">
                    Nazwa produktu *
                  </label>
                  <input
                    type="text"
                    value={product.name || ''}
                    onChange={(e) => setProduct({ ...product, name: e.target.value })}
                    className="w-full px-4 py-2 bg-zinc-700/50 rounded-lg text-white border border-zinc-600 focus:border-purple-500 focus:outline-none transition-all"
                    placeholder="np. Ekspozytor kosmetyczny Premium"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-400 mb-2">
                    Kategoria produktu *
                  </label>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                    {productCategories.map((category) => (
                      <button
                        key={category.id}
                        onClick={() => setProduct({ 
                          ...product, 
                          category: category.id,
                          subcategory: '',
                          multiplier: category.multiplier,
                          waste: category.waste
                        })}
                        className={`p-4 rounded-xl border-2 transition-all ${
                          product.category === category.id
                            ? 'border-purple-500 bg-purple-500/10'
                            : 'border-zinc-700 hover:border-zinc-600'
                        }`}
                      >
                        <div className="flex items-center gap-3 mb-2">
                          {category.icon}
                          <span className="font-medium">{category.name}</span>
                        </div>
                        <p className="text-xs text-gray-400 text-left">
                          {category.description}
                        </p>
                      </button>
                    ))}
                  </div>
                </div>

                {/* Podkategorie dla ekspozytorów */}
                {selectedCategory?.subcategories && (
                  <div>
                    <label className="block text-sm font-medium text-gray-400 mb-2">
                      Typ {selectedCategory.name.toLowerCase()}
                    </label>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                      {selectedCategory.subcategories.map((sub) => (
                        <button
                          key={sub.id}
                          onClick={() => setProduct({ ...product, subcategory: sub.id })}
                          className={`p-3 rounded-lg border-2 transition-all ${
                            product.subcategory === sub.id
                              ? 'border-purple-500 bg-purple-500/10'
                              : 'border-zinc-700 hover:border-zinc-600'
                          }`}
                        >
                          <span className="text-sm font-medium">{sub.name}</span>
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                <div>
                  <label className="block text-sm font-medium text-gray-400 mb-2">
                    Opis produktu
                  </label>
                  <textarea
                    value={product.description || ''}
                    onChange={(e) => setProduct({ ...product, description: e.target.value })}
                    rows={3}
                    className="w-full px-4 py-2 bg-zinc-700/50 rounded-lg text-white border border-zinc-600 focus:border-purple-500 focus:outline-none transition-all resize-none"
                    placeholder="Szczegółowy opis produktu..."
                  />
                </div>
              </div>
            </div>

            {/* Materiał i parametry */}
            <div>
              <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                <Layers className="w-5 h-5 text-blue-400" />
                Materiał i parametry techniczne
              </h3>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-400 mb-2">
                    Materiał *
                  </label>
                  <select
                    value={product.material || 'plexi'}
                    onChange={(e) => setProduct({ ...product, material: e.target.value })}
                    className="w-full px-4 py-2 bg-zinc-700/50 rounded-lg text-white border border-zinc-600 focus:border-purple-500 focus:outline-none transition-all"
                  >
                    {materials.map((material) => (
                      <option key={material.id} value={material.id}>
                        {material.name} - {material.basePrice} zł/m²/mm
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-400 mb-2">
                    Grubość standardowa (mm)
                  </label>
                  <input
                    type="number"
                    value={product.thickness || 3}
                    onChange={(e) => setProduct({ ...product, thickness: Number(e.target.value) })}
                    className="w-full px-4 py-2 bg-zinc-700/50 rounded-lg text-white border border-zinc-600 focus:border-purple-500 focus:outline-none transition-all"
                    min="0.5"
                    step="0.5"
                  />
                </div>

                {product.category !== 'kaseton' && (
                  <div>
                    <label className="block text-sm font-medium text-gray-400 mb-2">
                      Cena bazowa za m² (zł)
                    </label>
                    <input
                      type="number"
                      value={product.price_per_m2 || ''}
                      onChange={(e) => setProduct({ ...product, price_per_m2: Number(e.target.value) })}
                      className="w-full px-4 py-2 bg-zinc-700/50 rounded-lg text-white border border-zinc-600 focus:border-purple-500 focus:outline-none transition-all"
                      min="0"
                      step="10"
                      placeholder="0"
                    />
                  </div>
                )}

                {product.category === 'kaseton' && (
                  <div>
                    <label className="block text-sm font-medium text-gray-400 mb-2">
                      Typ kasetonu
                    </label>
                    <select
                      value={product.product_params?.kasetonType || 'plexi'}
                      onChange={(e) => setProduct({ 
                        ...product, 
                        product_params: {
                          ...product.product_params,
                          kasetonType: e.target.value as 'plexi' | 'dibond'
                        }
                      })}
                      className="w-full px-4 py-2 bg-zinc-700/50 rounded-lg text-white border border-zinc-600 focus:border-purple-500 focus:outline-none transition-all"
                    >
                      <option value="plexi">Kaseton z plexi - 1550 zł/m²</option>
                      <option value="dibond">Kaseton z dibond - 1400 zł/m²</option>
                    </select>
                  </div>
                )}

                <div>
                  <label className="block text-sm font-medium text-gray-400 mb-2">
                    Min. zamówienie (m²)
                  </label>
                  <input
                    type="number"
                    value={product.min_order || 0.25}
                    onChange={(e) => setProduct({ ...product, min_order: Number(e.target.value) })}
                    className="w-full px-4 py-2 bg-zinc-700/50 rounded-lg text-white border border-zinc-600 focus:border-purple-500 focus:outline-none transition-all"
                    min="0.01"
                    step="0.01"
                  />
                </div>
              </div>

              <div className="grid grid-cols-3 gap-4 mt-4">
                <div>
                  <label className="block text-sm font-medium text-gray-400 mb-2">
                    Max szerokość (mm)
                  </label>
                  <input
                    type="number"
                    value={product.max_width || 3050}
                    onChange={(e) => setProduct({ ...product, max_width: Number(e.target.value) })}
                    className="w-full px-4 py-2 bg-zinc-700/50 rounded-lg text-white border border-zinc-600 focus:border-purple-500 focus:outline-none transition-all"
                    min="1"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-400 mb-2">
                    Max wysokość (mm)
                  </label>
                  <input
                    type="number"
                    value={product.max_height || 2050}
                    onChange={(e) => setProduct({ ...product, max_height: Number(e.target.value) })}
                    className="w-full px-4 py-2 bg-zinc-700/50 rounded-lg text-white border border-zinc-600 focus:border-purple-500 focus:outline-none transition-all"
                    min="1"
                  />
                </div>

                {!['formatka', 'kaseton', 'ledon'].includes(product.category || '') && (
                  <div>
                    <label className="block text-sm font-medium text-gray-400 mb-2">
                      Max głębokość (mm)
                    </label>
                    <input
                      type="number"
                      value={product.max_depth || 500}
                      onChange={(e) => setProduct({ ...product, max_depth: Number(e.target.value) })}
                      className="w-full px-4 py-2 bg-zinc-700/50 rounded-lg text-white border border-zinc-600 focus:border-purple-500 focus:outline-none transition-all"
                      min="1"
                    />
                  </div>
                )}
              </div>

              {/* Parametry specyficzne */}
              {selectedCategory?.hasShelves && (
                <div className="mt-4">
                  <label className="block text-sm font-medium text-gray-400 mb-2">
                    Domyślna liczba półek
                  </label>
                  <input
                    type="number"
                    value={product.product_params?.shelves || 3}
                    onChange={(e) => setProduct({ 
                      ...product, 
                      product_params: {
                        ...product.product_params,
                        shelves: Number(e.target.value)
                      }
                    })}
                    className="w-full px-4 py-2 bg-zinc-700/50 rounded-lg text-white border border-zinc-600 focus:border-purple-500 focus:outline-none transition-all"
                    min="1"
                    max="10"
                  />
                </div>
              )}
            </div>

            {/* Cennik i marże */}
            <div>
              <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                <DollarSign className="w-5 h-5 text-emerald-400" />
                Cennik i marże
              </h3>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-400 mb-2">
                    Mnożnik ceny
                  </label>
                  <input
                    type="number"
                    value={product.multiplier || selectedCategory?.multiplier || 2.25}
                    onChange={(e) => setProduct({ ...product, multiplier: Number(e.target.value) })}
                    className="w-full px-4 py-2 bg-zinc-700/50 rounded-lg text-white border border-zinc-600 focus:border-purple-500 focus:outline-none transition-all"
                    min="1"
                    step="0.05"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-400 mb-2">
                    Procent odpadu (%)
                  </label>
                  <input
                    type="number"
                    value={(product.waste || selectedCategory?.waste || 0.05) * 100}
                    onChange={(e) => setProduct({ ...product, waste: Number(e.target.value) / 100 })}
                    className="w-full px-4 py-2 bg-zinc-700/50 rounded-lg text-white border border-zinc-600 focus:border-purple-500 focus:outline-none transition-all"
                    min="0"
                    max="50"
                    step="1"
                  />
                </div>
              </div>
            </div>

            {/* Tagi */}
            <div>
              <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                <Tag className="w-5 h-5 text-amber-400" />
                Tagi i wyszukiwanie
              </h3>

              <div className="space-y-3">
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={tagInput}
                    onChange={(e) => setTagInput(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        handleAddTag();
                      }
                    }}
                    placeholder="Dodaj tag (np. premium, LED, wodoodporny)"
                    className="flex-1 px-4 py-2 bg-zinc-700/50 rounded-lg text-white border border-zinc-600 focus:border-purple-500 focus:outline-none transition-all"
                  />
                  <Button onClick={handleAddTag} variant="secondary">
                    <Plus className="w-5 h-5" />
                    Dodaj
                  </Button>
                </div>

                <div className="flex flex-wrap gap-2">
                  {product.tags?.map((tag, index) => (
                    <motion.span
                      key={index}
                      initial={{ scale: 0 }}
                      animate={{ scale: 1 }}
                      className="px-3 py-1 bg-zinc-700/50 rounded-full text-sm text-gray-300 border border-zinc-600 flex items-center gap-2"
                    >
                      #{tag}
                      <button
                        onClick={() => handleRemoveTag(index)}
                        className="hover:text-red-400 transition-colors"
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </motion.span>
                  ))}
                </div>
              </div>
            </div>

            {/* Popularność */}
            <div>
              <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                <Star className="w-5 h-5 text-yellow-400" />
                Popularność i status
              </h3>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-400 mb-2">
                    Popularność produktu
                  </label>
                  <div className="space-y-2">
                    <input
                      type="range"
                      min="0"
                      max="100"
                      value={product.popularity || 50}
                      onChange={(e) => setProduct({ ...product, popularity: Number(e.target.value) })}
                      className="w-full accent-purple-500"
                    />
                    <div className="flex justify-between text-sm text-gray-400">
                      <span>Niszowy</span>
                      <span className="text-lg font-bold text-purple-500">{product.popularity}%</span>
                      <span>Bestseller</span>
                    </div>
                  </div>
                </div>

                <div className="flex items-center gap-4">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={product.is_active !== false}
                      onChange={(e) => setProduct({ ...product, is_active: e.target.checked })}
                      className="w-5 h-5 rounded border-zinc-600 bg-zinc-700 text-purple-500 focus:ring-purple-500"
                    />
                    <span className="text-white">Produkt aktywny</span>
                  </label>
                </div>
              </div>
            </div>

            {/* Actions */}
            <div className="flex gap-3 pt-4 border-t border-zinc-700">
              <Button
                onClick={onClose}
                variant="secondary"
                className="flex-1"
                disabled={saving}
              >
                Anuluj
              </Button>
              <Button
                onClick={handleSave}
                variant="primary"
                className="flex-1"
                disabled={saving || !product.name || !product.category || !product.material}
              >
                {saving ? (
                  <>
                    <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    Zapisywanie...
                  </>
                ) : (
                  <>
                    <Save className="w-5 h-5" />
                    {mode === 'create' ? 'Dodaj produkt' : 'Zapisz zmiany'}
                  </>
                )}
              </Button>
            </div>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};
